import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IdCard, Save, Trophy, Coins } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";

const lbl = "text-xs uppercase tracking-wider text-zinc-500";

export default function ProfileCard() {
  const { guildId } = useGuild();
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());

  useEffect(() => { api.get(`/guilds/${guildId}/modules/profile`).then((r) => setCfg(r.data)); }, [guildId]);
  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  const set = (k, v) => setCfg((prev) => ({ ...prev, config: { ...prev.config, [k]: v } }));
  const saveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/profile`, { enabled: cfg.enabled, config: cfg.config });
    toast.success("Profile card saved");
  };
  const accent = c.accent_color || "#0EA5E9";

  return (
    <div>
      <PageHead title="Profile Cards" desc="A rich /profile card blending each member's level, rank and coins.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Module</span><Toggle testid="profile-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="profile-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
          <h3 className="font-head font-semibold flex items-center gap-2"><IdCard className="w-4 h-4 text-volt" /> Card options</h3>
          <div className="flex items-center gap-3">
            <label className={lbl}>Accent color</label>
            <input type="color" data-testid="profile-color" value={accent} onChange={(e) => set("accent_color", e.target.value)} className="h-9 w-16 bg-black border border-zinc-800 rounded-md cursor-pointer" />
            <span className="text-sm text-zinc-400 font-mono">{accent}</span>
          </div>
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300 flex items-center gap-2"><Trophy className="w-4 h-4 text-volt" /> Show server rank (#)</span><Toggle testid="show-rank" checked={c.show_rank !== false} onChange={(v) => set("show_rank", v)} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300 flex items-center gap-2"><Coins className="w-4 h-4 text-volt" /> Show economy balance</span><Toggle testid="show-coins" checked={c.show_coins !== false} onChange={(v) => set("show_coins", v)} /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-zinc-300">Show message count</span><Toggle testid="show-messages" checked={c.show_messages !== false} onChange={(v) => set("show_messages", v)} /></div>
          </div>
          <p className="text-xs text-zinc-600">Members view their card in Discord with <code className="text-volt">/profile</code>. It combines Leveling and Economy data automatically.</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Preview</h3>
          <div data-testid="profile-preview" className="rounded-md overflow-hidden border-l-4 force-dark bg-[#111214]" style={{ borderColor: accent }}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                <img src="https://api.dicebear.com/7.x/bottts/svg?seed=Member" alt="" className="w-12 h-12 rounded-full bg-zinc-800" />
                <div className="font-semibold">NightHawk's Profile</div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4 text-center">
                <div><div className="text-[10px] uppercase text-zinc-500">Level</div><div className="text-lg font-bold" style={{ color: accent }}>12</div></div>
                {c.show_rank !== false && <div><div className="text-[10px] uppercase text-zinc-500">Rank</div><div className="text-lg font-bold">#4</div></div>}
                <div><div className="text-[10px] uppercase text-zinc-500">XP</div><div className="text-lg font-bold">43,200</div></div>
              </div>
              <div className="mt-3">
                <div className="h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full rounded-full" style={{ width: "62%", background: accent }} /></div>
                <div className="text-[11px] text-zinc-500 mt-1">62% to level 13</div>
              </div>
              {c.show_coins !== false && <div className="mt-3 text-sm text-zinc-300">💰 👛 🪙 8,540 · 🏦 🪙 12,000</div>}
              {c.show_messages !== false && <div className="mt-1 text-xs text-zinc-500">💬 3,204 messages</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
